import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Store,
  MapPin,
  Truck,
  ArrowRight,
  TrendingUp,
  Percent,
  Clock,
  ShieldCheck,
  CheckCircle,
  FileText,
  HelpCircle,
  ShoppingBag,
} from "lucide-react";

const BecomeASellerLanding = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col justify-start">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-border/40 bg-gradient-to-b from-indigo-50/20 via-background to-background dark:from-indigo-950/10">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        
        <div className="container mx-auto px-4 text-center max-w-4xl space-y-6">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold tracking-wide border border-primary/20 bg-primary/5 text-primary rounded-full hover:bg-primary/10 transition-colors animate-pulse">
            🚀 Now Onboarding Patna & Bihar Merchants
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none">
            Grow Your Local Retail Business With{" "}
            <span className="bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 bg-clip-text text-transparent dark:from-violet-400 dark:to-cyan-400">
              Bihari Mall
            </span>
          </h1>
          
          <p className="text-muted-foreground text-base sm:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed">
            Take your physical shop digital. List your items, connect with neighboring customers, manage orders, and deliver using your own staff or our automated delivery network.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Button size="lg" className="h-12 px-8 text-base font-semibold shadow-lg shadow-primary/20 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl transition-all hover:scale-105" asChild>
              <Link to="/auth/register-store">
                Register Your Store <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-8 text-base font-semibold border-border bg-card rounded-xl hover:bg-secondary/40 transition-all" asChild>
              <Link to="/help">
                Learn How it Works
              </Link>
            </Button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto pt-14 border-t border-border/40 mt-12 text-center">
            <div className="space-y-1">
              <p className="text-3xl sm:text-4xl font-extrabold text-foreground">1,200+</p>
              <p className="text-xs sm:text-sm text-muted-foreground font-semibold uppercase tracking-wider">Active Sellers</p>
            </div>
            <div className="space-y-1">
              <p className="text-3xl sm:text-4xl font-extrabold text-foreground">25 Mins</p>
              <p className="text-xs sm:text-sm text-muted-foreground font-semibold uppercase tracking-wider">Average Delivery Time</p>
            </div>
            <div className="col-span-2 md:col-span-1 space-y-1">
              <p className="text-3xl sm:text-4xl font-extrabold text-foreground">0%</p>
              <p className="text-xs sm:text-sm text-muted-foreground font-semibold uppercase tracking-wider">Listing Fee</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us / Features */}
      <section className="container mx-auto px-4 py-20 max-w-6xl">
        <div className="text-center space-y-3 mb-16">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Everything You Need to Succeed</h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            Our platform provides state-of-the-art tools designed specifically for local neighborhood merchants.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-violet-500/10 dark:bg-violet-500/20 p-3 w-fit text-violet-600 dark:text-violet-400 mb-3">
                <Store className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Instant Digital Store</CardTitle>
              <CardDescription className="text-sm">
                Get a customized storefront page instantly. Categorize inventory, highlight featured items, and manage live stock levels effortlessly.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-blue-500/10 dark:bg-blue-500/20 p-3 w-fit text-blue-600 dark:text-blue-400 mb-3">
                <MapPin className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Target Local Customers</CardTitle>
              <CardDescription className="text-sm">
                Target customers residing strictly within your defined delivery radius. Tap into neighboring demand with ease.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-cyan-500/10 dark:bg-cyan-500/20 p-3 w-fit text-cyan-600 dark:text-cyan-400 mb-3">
                <Truck className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Flexible Deliveries</CardTitle>
              <CardDescription className="text-sm">
                Fulfill orders using your own delivery boys, or utilize the Bihari Mall automated rider pool to deliver in minutes.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 p-3 w-fit text-emerald-600 dark:text-emerald-400 mb-3">
                <TrendingUp className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Rich Dashboards</CardTitle>
              <CardDescription className="text-sm">
                Monitor business health with comprehensive reports showing daily order graphs, peak hours, and product popularity.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-amber-500/10 dark:bg-amber-500/20 p-3 w-fit text-amber-600 dark:text-amber-400 mb-3">
                <Percent className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Promotions & Coupons</CardTitle>
              <CardDescription className="text-sm">
                Create promotional discount coupons, offer free deliveries above specific transaction amounts, and host weekend flash sales.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="rounded-xl bg-red-500/10 dark:bg-red-500/20 p-3 w-fit text-red-600 dark:text-red-400 mb-3">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Secure Transactions</CardTitle>
              <CardDescription className="text-sm">
                Enjoy transparent wallet settlement cycles directly deposited into your bank account, alongside safe digital wallet integrations.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* How it Works / Timeline */}
      <section className="bg-secondary/20 py-20 border-y border-border/40">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Onboarding in 4 Easy Steps</h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
              Get active on the customer app storefront in less than 24 hours.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-4 relative">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-border -z-10" />
            
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg border-4 border-background shadow-md">
                1
              </div>
              <h3 className="font-semibold text-lg">Apply Online</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Fill the unified merchant application form in 5 minutes.
              </p>
            </div>

            <div className="flex flex-col items-center text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg border-4 border-background shadow-md">
                2
              </div>
              <h3 className="font-semibold text-lg">Verification</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Our admin verifies your shop coordinates and KYC details.
              </p>
            </div>

            <div className="flex flex-col items-center text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg border-4 border-background shadow-md">
                3
              </div>
              <h3 className="font-semibold text-lg">Upload Catalog</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                List your items with custom prices, stock details, and photos.
              </p>
            </div>

            <div className="flex flex-col items-center text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg border-4 border-background shadow-md">
                4
              </div>
              <h3 className="font-semibold text-lg">Start Earning</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Accept delivery orders and watch your sales grow!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="container mx-auto px-4 py-20 max-w-3xl">
        <div className="text-center space-y-3 mb-12">
          <HelpCircle className="h-10 w-10 text-primary mx-auto mb-2" />
          <h2 className="text-3xl font-bold tracking-tight">Frequently Asked Questions</h2>
          <p className="text-muted-foreground">
            Clear up your queries before starting your onboarding process.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full border rounded-2xl bg-card p-6 shadow-sm">
          <AccordionItem value="item-1" className="border-b py-2">
            <AccordionTrigger className="text-left font-semibold hover:no-underline">
              Are there any registration fees or listing charges?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">
              No, registering your store on Bihari Mall and listing your products is completely free. We do not charge any setup, listing, or recurring fees.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2" className="border-b py-2">
            <AccordionTrigger className="text-left font-semibold hover:no-underline">
              How does delivery fulfillment work?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">
              You can choose between two methods: (1) **Self Delivery**, where your own store staff delivers the package within your radius, or (2) **Platform Delivery**, where Bihari Mall's network of delivery partners automatically picks up and delivers the order.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-3" className="border-b py-2">
            <AccordionTrigger className="text-left font-semibold hover:no-underline">
              When do I get paid for my sales?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">
              All digital payments are collected in your wallet. Settlements are transferred directly to your registered bank account on a regular weekly basis, or can be initiated manually directly from the merchant dashboard.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-4" className="py-2">
            <AccordionTrigger className="text-left font-semibold hover:no-underline">
              What documents are required to apply?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">
              You can get started immediately with just your basic contact information and store location. To unlock full features and payouts, you can optionally provide your PAN card number and GSTIN details under the KYC section.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* CTA Final Banner */}
      <section className="bg-gradient-to-r from-violet-600 to-indigo-700 py-16 text-center text-white border-t">
        <div className="container mx-auto px-4 max-w-xl space-y-6">
          <h2 className="text-3xl font-extrabold">Ready to take your shop online?</h2>
          <p className="text-indigo-100 text-base leading-relaxed">
            Join hundreds of local merchants in Bihar who are multiplying their daily sales using the Bihari Mall partner app.
          </p>
          <Button size="lg" className="h-12 px-8 text-base font-semibold bg-white text-indigo-700 hover:bg-indigo-50 shadow-xl rounded-xl transition-all hover:scale-105" asChild>
            <Link to="/auth/register-store">
              Start Your Onboarding <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BecomeASellerLanding;
